import axios, { type AxiosRequestConfig } from 'axios'
import { httpClient } from '@/shared/services/http'
import type { TenantApiResponse } from './roles/roles.types'

/**
 * Extract a human-readable message from a failed request. The tenant-admin API
 * returns `message` as either a string or an array of validation strings.
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined
    const message = data?.message
    if (Array.isArray(message)) return message.join(', ')
    return message || error.message || fallback
  }
  if (error instanceof Error) return error.message
  return fallback
}

/**
 * Thin wrapper around the shared `httpClient` for tenant-admin endpoints.
 *
 * It mirrors ADP's `apiRequest`: it unwraps the `{ success, status, message,
 * data }` envelope and throws a plain `Error` with a readable message on
 * failure. `httpClient` already attaches the `Bearer` token and the
 * `x-tenant-name` header via its request interceptor.
 */
export async function tenantApiRequest<T>(
  requestConfig: AxiosRequestConfig,
  fallbackMessage = 'Request failed'
): Promise<T> {
  try {
    const response = await httpClient.request<TenantApiResponse<T>>(requestConfig)
    const res = response.data

    if (!res.success) {
      const message = Array.isArray(res.message)
        ? res.message.join(', ')
        : res.message
      throw new Error(message || fallbackMessage)
    }

    return res.data
  } catch (error) {
    throw new Error(extractErrorMessage(error, fallbackMessage))
  }
}
